import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import Topbar from "@/components/layout/Topbar";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { sourceSystem: "", sourceField: "", unifiedField: "", description: "" };

export default function DataDictionary() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // null | EMPTY | item
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/data-dictionary")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Failed to load data dictionary"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = items.filter((i) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return (
      i.sourceSystem.toLowerCase().includes(needle) ||
      i.sourceField.toLowerCase().includes(needle) ||
      i.unifiedField.toLowerCase().includes(needle) ||
      (i.description || "").toLowerCase().includes(needle)
    );
  });

  const isAdmin = user?.role === "SUPER_ADMIN";

  const save = async () => {
    try {
      if (editing.id) {
        await api.patch(`/data-dictionary/${editing.id}`, {
          sourceSystem: editing.sourceSystem,
          sourceField: editing.sourceField,
          unifiedField: editing.unifiedField,
          description: editing.description,
        });
        toast.success("Mapping updated");
      } else {
        await api.post("/data-dictionary", editing);
        toast.success("Mapping added");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this mapping?")) return;
    try {
      await api.delete(`/data-dictionary/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <>
      <Toaster position="top-right" richColors />
      <Topbar
        title="Data Dictionary"
        subtitle="Source → Unified field mapping across all ERPs"
        right={
          isAdmin && (
            <button
              type="button"
              onClick={() => setEditing({ ...EMPTY })}
              data-testid="dd-add-button"
              className="flex h-9 items-center gap-1.5 rounded-sm bg-brand-orange px-3 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#C1701C]"
            >
              <Plus className="h-3.5 w-3.5" />
              Add mapping
            </button>
          )
        }
      />

      <div className="p-6 space-y-4 animate-fade-in" data-testid="data-dictionary">
        <div>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="dd-search"
            placeholder="Search source system, field, unified field…"
            className="h-10 w-full max-w-md rounded-sm border border-slate-200 bg-white px-3 text-sm outline-none transition-colors focus:border-navy-600"
          />
        </div>

        <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                <th className="px-5 py-3 text-left">Source System</th>
                <th className="px-5 py-3 text-left">Source Field</th>
                <th className="px-5 py-3 text-left">Unified Field</th>
                <th className="px-5 py-3 text-left">Description</th>
                {isAdmin && <th className="px-5 py-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-slate-400">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-slate-400">
                    No mappings found.
                  </td>
                </tr>
              )}
              {filtered.map((it) => (
                <tr
                  key={it.id}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                  data-testid={`dd-row-${it.id}`}
                >
                  <td className="px-5 py-3">
                    <span className="rounded-sm bg-navy-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-white">
                      {it.sourceSystem}
                    </span>
                  </td>
                  <td className="px-5 py-3 mono text-xs text-slate-700">
                    {it.sourceField}
                  </td>
                  <td className="px-5 py-3 mono text-xs font-medium text-brand-orange">
                    {it.unifiedField}
                  </td>
                  <td className="px-5 py-3 text-slate-500">
                    {it.description || "—"}
                  </td>
                  {isAdmin && (
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => setEditing(it)}
                          data-testid={`dd-edit-${it.id}`}
                          className="rounded-sm p-1.5 text-slate-400 hover:bg-slate-100 hover:text-navy-600"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(it.id)}
                          data-testid={`dd-delete-${it.id}`}
                          className="rounded-sm p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit modal */}
      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-navy-600/70 p-4 backdrop-blur-sm"
          data-testid="dd-modal"
        >
          <div className="w-full max-w-lg rounded-md border border-slate-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div className="font-display text-base font-semibold text-navy-600">
                {editing.id ? "Edit Mapping" : "Add Mapping"}
              </div>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-sm p-1 text-slate-400 hover:text-navy-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4 p-5">
              {["sourceSystem", "sourceField", "unifiedField", "description"].map(
                (f) => (
                  <div key={f}>
                    <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                      {f}
                    </label>
                    <input
                      value={editing[f] || ""}
                      onChange={(e) =>
                        setEditing({ ...editing, [f]: e.target.value })
                      }
                      data-testid={`dd-input-${f}`}
                      className="mt-1.5 block h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm outline-none focus:border-navy-600"
                    />
                  </div>
                )
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="h-9 rounded-sm border border-slate-200 px-3 text-xs font-semibold uppercase tracking-widest text-slate-600 hover:border-navy-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                data-testid="dd-save-button"
                className="h-9 rounded-sm bg-brand-orange px-3 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#C1701C]"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
